import React, { useCallback, useState } from 'react';
import identityApi from '../../clients/IdentityClient';
import { CreditPackageProduct } from '../../models/CreditPackageProduct';
import { PaymentIntentRequest } from '../../models/requestBodies/PaymentIntentRequest';
import { PaymentIntentResponse } from '../../models/responseBodies/PaymentIntentResponse';
import { GiftFormState } from '../../models/GiftFormState';
import { getDefaultTimePickerDateTime } from '../../utils/time';
import TextInputField from '../shared/TextInputField';

// Declare dataLayer for Google Tag Manager
declare global {
  interface Window {
    dataLayer: any[];
  }
}

interface UserInputFormProps {
  selectedProduct: CreditPackageProduct | null;
  clientSecret: string | null;
  formData: GiftFormState;
  onFormChange: (data: Partial<GiftFormState>) => void;
  onClientSecretChange: (secret: string | null) => void;
}

// Email validation regex pattern
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UserInputForm: React.FC<UserInputFormProps> = ({ 
  selectedProduct, 
  clientSecret,
  formData,
  onFormChange,
  onClientSecretChange,
}) => {
  const [error, setError] = useState<string>('');
  const [successCopy, setSuccessCopy] = useState<string>('');
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [emailErrors, setEmailErrors] = useState<{ buyerEmail?: string; recipientEmail?: string }>({});
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [tempDateTimeValue, setTempDateTimeValue] = useState<string | null>(null);
  
  // Check if terms have been accepted (stored in localStorage)
  const TERMS_ACCEPTANCE_KEY = 'worldOfRacing_termsAccepted';
  
  const hasAcceptedTerms = (): boolean => {
    const accepted = localStorage.getItem(TERMS_ACCEPTANCE_KEY);
    return accepted === 'true';
  };
  
  const [termsAccepted, setTermsAccepted] = useState<boolean>(hasAcceptedTerms());
  
  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const accepted = e.target.checked;
    setTermsAccepted(accepted);
    if (accepted) {
      localStorage.setItem(TERMS_ACCEPTANCE_KEY, 'true');
    } else {
      localStorage.removeItem(TERMS_ACCEPTANCE_KEY);
    }
  };

  // Helper function to get current local datetime in format for datetime-local input (YYYY-MM-DDTHH:mm)
  // Defaults to 00:00 time
  const getCurrentLocalDateTime = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00`;
  };

  // Helper function to convert local datetime to ISO string with timezone offset
  const convertLocalToISOWithOffset = (localDateTimeString: string): string => {
    // datetime-local input gives format: "YYYY-MM-DDTHH:mm" (no timezone)
    // Create a Date object which interprets it as local time
    const localDate = new Date(localDateTimeString);

    // Get timezone offset in minutes (negative means ahead of UTC, positive means behind)
    const offsetMinutes = localDate.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const offsetSign = offsetMinutes <= 0 ? '+' : '-';

    // Format: YYYY-MM-DDTHH:mm:ss+HH:mm or YYYY-MM-DDTHH:mm:ss-HH:mm
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const seconds = String(localDate.getSeconds()).padStart(2, '0');
    const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetStr}`;
  };

  const createGiftIntent = useCallback(async (): Promise<void> => {
    if (!selectedProduct) {
      throw new Error('Please select a credit package');
    }

    setIsCreatingIntent(true);
    setError('');

    try {
      const paymentIntentRequest: PaymentIntentRequest = {
        productId: selectedProduct.id,
        purchaserEmail: formData.buyerEmail,
        purchaserFirstName: formData.buyerFirstName,
        purchaserLastName: formData.buyerLastName,
        isGift: formData.isGift,
        recipientEmail: formData.isGift ? (formData.recipientEmail || null) : null,
        recipientFirstName: formData.isGift ? (formData.recipientFirstName || null) : null,
        recipientLastName: formData.isGift ? (formData.recipientLastName || null) : null,
        personalMessage: formData.isGift ? (formData.message || null) : null,
        deliverAt: formData.isGift ? (formData.deliverAt ? convertLocalToISOWithOffset(formData.deliverAt) : null) : null,
      };

      const response: PaymentIntentResponse = await identityApi.intentPayment(paymentIntentRequest);

      if (response && response.clientSecret) {
        onClientSecretChange(response.clientSecret);
      } else {
        throw new Error('Invalid response from payment intent');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create payment intent';
      setError(message);
    } finally {
      setIsCreatingIntent(false);
    }
  }, [selectedProduct, formData, onClientSecretChange]);

  const validateEmail = (email: string): boolean => {
    return emailRegex.test(email);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    
    // For datetime-local inputs, ensure time defaults to 00:00 if not specified
    let processedValue = value;
    if (name === 'deliverAt' && value) {
      // If value contains date but no time, or time is incomplete, default to 00:00
      if (value.includes('T') && !value.match(/T\d{2}:\d{2}$/)) {
        // If time portion is missing or incomplete, set to 00:00
        const datePart = value.split('T')[0];
        processedValue = `${datePart}T00:00`;
      } else if (!value.includes('T') && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // If only date is provided, add 00:00
        processedValue = `${value}T00:00`;
      }
      // Clear temporary value when user actually selects a value
      if (tempDateTimeValue) {
        setTempDateTimeValue(null);
      }
    }
    
    onFormChange({ [name]: processedValue });
    
    // Clear field errors when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (name === 'buyerEmail' || name === 'recipientEmail') {
      if (value && !validateEmail(value)) {
        setEmailErrors((prev) => ({
          ...prev,
          [name]: 'Please enter a valid email address',
        }));
      } else {
        setEmailErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name as keyof typeof newErrors];
          return newErrors;
        });
      }
    }
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
  };

  const handleCreateIntent = async () => {
    setError('');
    setSuccessCopy('');
    setFieldErrors({});
    setEmailErrors({});

    // Validate selected product
    if (!selectedProduct) {
      setError('Please select a credit package.');
      return;
    }

    // Validate all required fields
    const errors: { [key: string]: string } = {};

    if (!formData.buyerFirstName || formData.buyerFirstName.trim() === '') {
      errors.buyerFirstName = 'First name is required';
    }

    if (!formData.buyerLastName || formData.buyerLastName.trim() === '') {
      errors.buyerLastName = 'Last name is required';
    }

    if (!formData.buyerEmail || formData.buyerEmail.trim() === '') {
      errors.buyerEmail = 'Email is required';
    } else if (!validateEmail(formData.buyerEmail)) {
      errors.buyerEmail = 'Please enter a valid email address';
    }

    // Only validate gift fields if it's a gift
    if (formData.isGift) {
      if (!formData.recipientFirstName || formData.recipientFirstName.trim() === '') {
        errors.recipientFirstName = 'First name is required';
      }

      if (!formData.recipientLastName || formData.recipientLastName.trim() === '') {
        errors.recipientLastName = 'Last name is required';
      }

      if (!formData.recipientEmail || formData.recipientEmail.trim() === '') {
        errors.recipientEmail = 'Email is required';
      } else if (!validateEmail(formData.recipientEmail)) {
        errors.recipientEmail = 'Please enter a valid email address';
      }

      if (!formData.deliverAt || formData.deliverAt.trim() === '') {
        errors.deliverAt = 'Deliver At date and time is required';
      }
    }

    // Set errors if any
    if (Object.keys(errors).length > 0) {
      const emailErrorsObj: { buyerEmail?: string; recipientEmail?: string } = {};
      const fieldErrorsObj: { [key: string]: string } = {};

      Object.keys(errors).forEach((key) => {
        if (key === 'buyerEmail' || key === 'recipientEmail') {
          emailErrorsObj[key as 'buyerEmail' | 'recipientEmail'] = errors[key];
        } else {
          fieldErrorsObj[key] = errors[key];
        }
      });

      setEmailErrors(emailErrorsObj);
      setFieldErrors(fieldErrorsObj);
      return;
    }

    await createGiftIntent();
  };

  return (
    <div className="checkout-card card">
      <form onSubmit={handleFormSubmit} className="checkout-form" noValidate>
        {/* Gift Checkbox */}
        <div className="mb-4">
          <div className="d-flex align-items-center gift-checkbox-container">
            <div className="form-check">
              <input
                className="form-check-input checkout-checkbox"
                type="checkbox"
                id="isGiftToggle"
                checked={formData.isGift}
                onChange={(e) => onFormChange({ isGift: e.target.checked })}
                disabled={!!clientSecret}
              />
              <label className="form-check-label text-accent" htmlFor="isGiftToggle">
              Send race credits as a gift
              </label>
            </div>
          </div>
          <p className="checkout-lead">
            Send Race Credits as a gift and surprise someone with real racing. Choose your package and we'll deliver the Race Credits on the date you pick.
          </p>
        </div>

        {/* Your info section */}
        <div>
          <label className="form-label text-accent">Your info</label>
          <div className="card-element-wrapper">
            <div className="row g-3">
              <div className="col-md-6">
                <TextInputField
                  id="buyerFirstName"
                  name="buyerFirstName"
                  type="text"
                  placeholder="First name"
                  value={formData.buyerFirstName}
                  onChange={handleChange}
                  error={fieldErrors.buyerFirstName}
                  disabled={!!clientSecret}
                  required
                />
              </div>
              <div className="col-md-6">
                <TextInputField
                  id="buyerLastName"
                  name="buyerLastName"
                  type="text"
                  placeholder="Last name"
                  value={formData.buyerLastName}
                  onChange={handleChange}
                  error={fieldErrors.buyerLastName}
                  disabled={!!clientSecret}
                  required
                />
              </div>
            </div>
            <div className="row g-3 mt-1">
              <div className="col-12">
                <TextInputField
                  id="buyerEmail"
                  name="buyerEmail"
                  type="email"
                  placeholder="Email"
                  value={formData.buyerEmail}
                  onChange={handleChange}
                  error={emailErrors.buyerEmail}
                  disabled={!!clientSecret}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Recipient info section - only show if gift */}
        {formData.isGift && (
          <>
            <div className="mt-4">
              <label className="form-label text-accent">Recipient info</label>
              <div className="card-element-wrapper">
                <div className="row g-3">
                  <div className="col-md-6">
                    <TextInputField
                      id="recipientFirstName"
                      name="recipientFirstName"
                      type="text"
                      placeholder="First name"
                      value={formData.recipientFirstName}
                      onChange={handleChange}
                      error={fieldErrors.recipientFirstName}
                      disabled={!!clientSecret}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <TextInputField
                      id="recipientLastName"
                      name="recipientLastName"
                      type="text"
                      placeholder="Last name"
                      value={formData.recipientLastName}
                      onChange={handleChange}
                      error={fieldErrors.recipientLastName}
                      disabled={!!clientSecret}
                      required
                    />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12">
                    <TextInputField
                      id="recipientEmail"
                      name="recipientEmail"
                      type="email"
                      placeholder="Email"
                      value={formData.recipientEmail}
                      onChange={handleChange}
                      error={emailErrors.recipientEmail}
                      disabled={!!clientSecret}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Deliver At field - only show if gift */}
            <div className="mt-4">
              <label className="form-label text-accent" htmlFor="deliverAt">
                Deliver At
              </label>
              <input
                id="deliverAt"
                name="deliverAt"
                type="datetime-local"
                className={`form-control ${fieldErrors.deliverAt ? 'is-invalid' : ''}`}
                value={formData.deliverAt || tempDateTimeValue || ''}
                onChange={handleChange}
                onBlur={(e) => {
                  // If user didn't actually select a value (still matches temp value), clear it
                  if (tempDateTimeValue && e.currentTarget.value === tempDateTimeValue && !formData.deliverAt) {
                    setTempDateTimeValue(null);
                    e.currentTarget.value = '';
                  } else if (e.currentTarget.value && e.currentTarget.value !== tempDateTimeValue) {
                    // User selected a value, clear temp reference
                    setTempDateTimeValue(null);
                  }
                }}
                onClick={(e) => {
                  if (clientSecret) return; // Don't open picker if form is disabled
                  
                  const inputElement = e.currentTarget;
                  
                  // If field is empty, set temporary value with next hour at 00 minutes
                  if (!formData.deliverAt && !tempDateTimeValue) {
                    const nextHourDateTime = getDefaultTimePickerDateTime();
                    setTempDateTimeValue(nextHourDateTime);
                    // Set the value directly on the input element
                    inputElement.value = nextHourDateTime;
                    
                    // Use double requestAnimationFrame to ensure React state update is processed
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        if ('showPicker' in inputElement && typeof (inputElement as any).showPicker === 'function') {
                          try {
                            (inputElement as any).showPicker();
                          } catch (err) {
                            inputElement.focus();
                          }
                        } else {
                          inputElement.focus();
                        }
                      });
                    });
                  } else {
                    // Field has value or temp already set, open picker immediately
                    if ('showPicker' in inputElement && typeof (inputElement as any).showPicker === 'function') {
                      try {
                        (inputElement as any).showPicker();
                      } catch (err) {
                        inputElement.focus();
                      }
                    } else {
                      inputElement.focus();
                    }
                  }
                }}
                min={getCurrentLocalDateTime()}
                disabled={!!clientSecret}
                required={formData.isGift}
                step="60"
              />
              {fieldErrors.deliverAt && (
                <div className="invalid-feedback invalid-feedback-visible">
                  {fieldErrors.deliverAt}
                </div>
              )}
            </div>

            {/* Personal message field */}
            <div className="mt-4">
              <label className="form-label text-accent" htmlFor="message">
                Personal message
              </label>
              <textarea
                id="message"
                name="message"
                rows={3}
                className="form-control"
                placeholder="Write a personal message that will be included in the email."
                value={formData.message}
                onChange={handleChange}
                disabled={!!clientSecret}
              />
            </div>
          </>
        )}


        {error && (
          <div className="alert alert-danger mt-3" role="alert">
            {error}
          </div>
        )}
        {successCopy && (
          <div className="alert alert-success mt-3" role="alert">
            {successCopy}
          </div>
        )}

        {/* Terms of Service Checkbox */}
        {!clientSecret && (
          <div className="mt-4">
            <div className="terms-checkbox-container">
              <input
                type="checkbox"
                id="termsCheckbox"
                checked={termsAccepted}
                onChange={handleTermsChange}
                className="form-check-input"
                disabled={!!clientSecret}
              />
              <label htmlFor="termsCheckbox" className="terms-checkbox-label">
                I agree to World of Racing Holdings, Inc's {' '}
                <a
                  href="https://www.worldofracing.com/membership-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-link"
                >
                  Terms of Service
                </a>
              </label>
            </div>
          </div>
        )}

        {!clientSecret && (
          <button 
            className="btn btn-primary w-100 mt-4" 
            type="button" 
            onClick={handleCreateIntent}
            disabled={isCreatingIntent || !selectedProduct || !termsAccepted}
          >
            {isCreatingIntent ? 'Creating payment intent...' : selectedProduct ? 'Go to payment' : 'Please select a package'}
          </button>
        )}
      </form>
    </div>
  );
};

export default UserInputForm;


